import React, { useState, useEffect, useMemo, useRef } from 'react';
import Grid from '@mui/material/Grid2';
import {
	Box,
	TextField,
	Typography,
	FormControl,
	Tooltip, 
	IconButton,
	Button,
	CircularProgress,
	RadioGroup,
	FormControlLabel,
	Radio,
} from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import debounce from 'lodash.debounce';
import ClearIcon from '@mui/icons-material/Clear';
import { Formik } from 'formik';
import * as Yup from "yup";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from "dayjs";
import moment from 'moment';
import { useNavigate } from "react-router-dom";
import { useStoreActions } from 'easy-peasy';
import { stringConstants } from '../../constants/stringConstants';
import NoteText from './NoteText';
import { customSortResults } from '../../utils/common';

const SearchForm = (props) => {
	const { orgId, configRules } = props;
	const inputRefComboBox = useRef(null);
	const inputRefProcedure = useRef(null);
	// const inputRefDiagnosis = useRef(null);
	const navigate = useNavigate();
	const commonAction = useStoreActions((actions) => actions.common);
	const paCheckAction = useStoreActions((actions) => actions.paCheck);
	const [openComboBox, setOpenComboBox] = useState(false);
	const [inputComboBoxValue, setInputComboBoxValue] = useState('');
	const [comboBoxLoader, setComboBoxLoader] = useState(false);
	const [comboBoxOptions, setComboBoxOptions] = useState([]);
	const [openProcedure, setOpenProcedure] = useState(false);
	const [inputProcedureValue, setInputProcedureValue] = useState('');
	const [procedureLoader, setProcedureLoader] = useState(false);
	const [procedureOptions, setProcedureOptions] = useState([]);
	// const [openDiagnosis, setOpenDiagnosis] = useState(false);
	// const [inputDiagnosisValue, setInputDiagnosisValue] = useState('');
	// const [diagnosisLoader, setDiagnosisLoader] = useState(false);
	// const [diagnosisOptions, setDiagnosisOptions] = useState([]);
	const [submitLoader, setSubmitLoader] = useState(false);

	const schema = Yup.object().shape({
    record_number: Yup.mixed()
      .nullable()
      .test('required', 'Field is required', value => {
        if (typeof value === 'string') {
          return !!value.trim();
        }
        if (typeof value === 'object' && value !== null) {
          // Accept if object has groupId or code
          return !!value.groupId || !!value.code;
        }
        return false;
      }),
		date_of_service: Yup.mixed()
			.nullable()
			.transform((value) => (dayjs.isDayjs(value) ? value.toDate() : value))
			.required("Field is required"),
    procedure_codes: Yup.array().min(1, 'Field is required').required(),
    // npi: Yup.string().matches(/^[0-9]+$/, "Only numbers are allowed").length(10, "The NPI must be 10 digits").nullable(),
		npi: Yup.string().when(['user_type'], ([userType], schema) => {
			if (userType === 'provider') {
				return schema
					.required('Field is required')
					.matches(/^\d{10}$/, 'NPI must be exactly 10 digits');
			}
			return schema.notRequired();
		}),
  });

	const initialValues = React.useMemo(() => ({
		user_type: 'member',
		record_number: null,
		procedure_codes: [],
		date_of_service: null,
		npi: '',
		// diagnosis_codes: [],
  }), []);

	const filterOptions = createFilterOptions({
    matchFrom: 'any',
    limit: 10000,
  });

	const [fieldData] = useState(initialValues);

	const getAPICall = async (query, type, isOnLoad = false) => {
		if(type === 'groups') {
			setComboBoxLoader(true);
		}
		if(type === 'procedures') {
      setProcedureLoader(true);
    }
		// if(type === 'diagnosis') {
    //   setDiagnosisLoader(true);
    // }

    await paCheckAction.getProcDiagCodes({"query": query ? query : '', "type": type, orgId: orgId})
      .then(function (response) {
				if(type === 'groups') {
          setComboBoxOptions(response?.data?.data?.records || []);
          if(!isOnLoad) {
            setTimeout(() => {
              if (inputRefComboBox.current) {
                inputRefComboBox.current.focus();   // focus input
              }
              setOpenComboBox(true);
            }, 300);
          }
        }
				if(type === 'procedures') {
          setProcedureOptions(response?.data?.data?.codes || []);
          if(!isOnLoad) {
            setTimeout(() => {
              if (inputRefProcedure.current) {
                inputRefProcedure.current.focus();   // focus input
              }
              setOpenProcedure(true); 
            }, 300);
          }
        }
				// if(type === 'diagnosis') {
        //   setDiagnosisOptions(response?.data?.data?.codes || []);
        //   if(!isOnLoad) {
        //     setTimeout(() => {
        //       if (inputRefDiagnosis.current) {
        //         inputRefDiagnosis.current.focus();   // focus input
        //       }
        //       setOpenDiagnosis(true); 
        //     }, 300);
        //   }
				// }
      })
      .catch(function (error) {
        console.log("error", error);
      })
      .finally(() => {
				setComboBoxLoader(false);
				setProcedureLoader(false);
				// setDiagnosisLoader(false);
      });
  };
	const fetchOptionsData = async (query, type) => {
    if (!query) return;
    await getAPICall(query, type);  
  };
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedFetch = useMemo(() => debounce(fetchOptionsData, 1000), []);
  const handleInputChange = (event, newInputValue, type = null) => {
    if (newInputValue && newInputValue.length >= 3) {
      debouncedFetch(newInputValue, type);
    }
  };

	function onFormSubmit(values, {resetForm, setErrors}) {
		setSubmitLoader(true);
		const procedure_codes = [];
		values.procedure_codes.forEach((element) => {
			if (element.code) {
				procedure_codes.push({"code": element.code, "title": element.title});
			}
		});
		// const diagnostic_codes = [];
		// values.diagnosis_codes.forEach((element) => {
		// 	if (element.code) {
		// 		diagnostic_codes.push({"code": element.code});
		// 	}
		// });
		values.id = orgId;
		values.date_of_service = moment(values.date_of_service).format('YYYY-MM-DD');
		values.procedure_codes = procedure_codes;
		// values.diagnostic_codes = diagnostic_codes;

		// delete values.diagnosis_codes;

		paCheckAction.getPACheckResults(values)
			.then(function (response) {
				setSubmitLoader(false);
				navigate(`/${values.id}/${stringConstants.PA_CHECK_RESULTS_URL}`, { state: { values } });
			})
			.catch(function (error) {
				// console.log('Error', error.response.data);
				setSubmitLoader(false);
				if (error.response && error.response.data && error.response.data.error_type && error.response.data.error_type === "validation_error") {
					const outputErrors = {};
					const errorlist = error.response.data.error;
					for (const item in errorlist) {
						outputErrors[Object.keys(errorlist[item])] =
							errorlist[item][Object.keys(errorlist[item])];
					}
					setErrors(outputErrors);
				} else {
					commonAction.setSubmitSnackbar({
						open: true,
						status: false,
						messages: error?.response?.data?.error || "An error occurred while submitting the form. Please try again.",
					});
				}
			});
	};

  useEffect(() => {
    // Fetch initial options for combo box on component mount
    getAPICall('', 'groups', true);
		getAPICall('', 'procedures', true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Box component="section" className="headerBox" style={{marginTop:'10px'}}>
				<Formik
					initialValues={fieldData}
					onSubmit={onFormSubmit}
					validationSchema={schema}
					enableReinitialize={true}
				>
					{(formikProps) => {
						const {
							values,
							touched,
							errors,
							handleChange,
							setFieldValue,
							handleBlur,
							handleSubmit,
							setFieldTouched,
						} = formikProps;
						return (
							<form onSubmit={handleSubmit} noValidate>
								{/** Main container */}
								<Grid container className="container" style={{width: '50%', float: 'left'}}>
										<Grid container className="container" spacing={2}>
											<Grid item size={{ xs: 12 }} style={{marginBottom: '20px'}}>
												<Typography><b>When to use this form:</b></Typography>
												<Typography>Use this form to determine if a service or item requires prior authorization from the health plan before you receive care.</Typography>
											</Grid>
											<Grid item size={{ xs: 12 }}>
												<Typography variant="h6" component="h6" className="filterFormLabel" style={{fontSize: '14px', fontWeight: 'bold', marginBottom: '5px'}}>
													<label>I am a</label>
												</Typography>
												<FormControl component="fieldset">
													<RadioGroup
														row
														name="user_type"
														value={values.user_type || 'member'}
														onChange={handleChange}
													>
														<FormControlLabel value="member" control={<Radio color="primary" />} label="Member / Patient" />
														<FormControlLabel value="provider" control={<Radio color="primary" />} label="Provider" />
													</RadioGroup>
												</FormControl>
											</Grid>

											{values.user_type === 'provider' && (
												<Grid item size={{ xs: 12 }}>
													<Typography variant="h6" component="h6" className="filterFormLabel" style={{fontSize: '14px', fontWeight: 'bold', marginBottom: '5px'}}>
														<label>National Provider ID *</label>
													</Typography>
													<TextField 
														// label="NPI Provider"
														placeholder="NPI Provider" 
														name="npi"
														required
														variant="outlined" 
														fullWidth 
														value={values?.npi}
														onChange={handleChange}
														onBlur={handleBlur}
														error={touched?.npi && Boolean(errors?.npi)}
														helperText={touched?.npi && errors?.npi}
													/>
													<p style={{marginTop: '15px', marginBottom: '15px'}}>Your NPI ensures we return the correct information and helps us understand who uses this tool.</p>
												</Grid>
											)}
											{configRules?.primary_lookup_entity?.toLowerCase() === "groups" ? (
												<Grid item size={{ xs: 12 }}>
													<Typography variant="h6" component="h6" className="filterFormLabel" style={{fontSize: '14px', fontWeight: 'bold', marginBottom: '5px'}}>
														<label>{configRules?.pa_check_record_number_label ? configRules?.pa_check_record_number_label : configRules?.primary_lookup_entity === "groups" ? "Member Group Number *" : "Member Number *"}</label>
													</Typography>
													<FormControl
														variant="outlined"
														fullWidth
														className={``}
													>
														<Autocomplete
															open={openComboBox}
															onOpen={() => setOpenComboBox(true)}
															onClose={() => {
																setOpenComboBox(false);
                                // setInputComboBoxValue("");
															}}
															options={customSortResults(comboBoxOptions, inputComboBoxValue)}
															getOptionLabel={(option) =>
																`${option.groupId} - ${option.groupName}`
															}
															value={values.record_number || null}
															inputValue={inputComboBoxValue}
															onChange={(event, newValue) => {
																setFieldValue("record_number", newValue);
																setInputComboBoxValue(newValue ? `${newValue.groupId} - ${newValue.groupName}` : "");
															}}
															filterOptions={filterOptions}
															onInputChange={(event, newInputValue, reason) => {
																if (reason === "input") {
																	setInputComboBoxValue(newInputValue);
																	if(!newInputValue) {
																		getAPICall('', 'groups', true);
																	} else {
																		handleInputChange(event, newInputValue, "groups");
																	}
																} else if (reason === "clear") {
																	setInputComboBoxValue("");
																	setFieldValue("record_number", null);
																	getAPICall('', 'groups', true);
																}
															}}
															renderInput={(params) => (
																<TextField
																	{...params}
																	fullWidth
																	name="record_number"
																	placeholder={configRules?.pa_check_record_number_label ? configRules?.pa_check_record_number_label : configRules?.primary_lookup_entity === "groups" ? "Member Group Number *" : "Member Number *"}
																	variant="outlined"
																	error={touched.record_number && Boolean(errors.record_number)}
																	helperText={touched.record_number && errors.record_number}
																	InputProps={{
																		...params.InputProps,
																		endAdornment: (
																			<>
																				{comboBoxLoader ? (
																					<CircularProgress color="inherit" size={20} />
																				) : null}
																				{params.InputProps.endAdornment}
																			</>
																		),
																	}}
																/>
															)}
															clearOnEscape
															readOnly={comboBoxLoader}
														/>
													</FormControl>
												</Grid>
											) : (
												<Grid item size={{ xs: 12 }}>
													<Typography variant="h6" component="h6" className="filterFormLabel" style={{fontSize: '14px', fontWeight: 'bold', marginBottom: '5px'}}>
														<label>{configRules?.pa_check_record_number_label ? configRules?.pa_check_record_number_label : configRules?.primary_lookup_entity === "groups" ? "Member Group Number *" : "Member Number *"}</label>
													</Typography>
													<TextField
														placeholder={configRules?.pa_check_record_number_label ? configRules?.pa_check_record_number_label : configRules?.primary_lookup_entity === "groups" ? "Member Group Number *" : "Member Number *"}
														name="record_number"
														variant="outlined" 
														required
														fullWidth 
														value={values?.record_number}
														onChange={handleChange}
														onBlur={handleBlur}
														error={touched?.record_number && Boolean(errors?.record_number)}
														helperText={touched?.record_number && errors?.record_number}
													/>
													<p style={{marginTop: '15px', marginBottom: '15px'}}>The member group number is located on the member ID card.</p>
												</Grid>
											)}
											
											<Grid item size={{ xs: 12}} style={{position: 'relative'}}>
												<Typography variant="h6" component="h6" className="filterFormLabel" style={{fontSize: '14px', fontWeight: 'bold', marginBottom: '5px'}}>
													<label>Service Date *</label>
												</Typography>
												<DesktopDatePicker
													value={values?.date_of_service && dayjs(values?.date_of_service).isValid() ? dayjs(values?.date_of_service) : null}
													onChange={(value) => {
														setFieldTouched("date_of_service", true);
														if (dayjs(value).isValid()) {
															setFieldValue("date_of_service", value.toDate());
														}
														// Do not set to null if invalid, just let the user finish typing
													}}
													minDate={dayjs("1900-01-01")}
													slotProps={{
														textField: {
															fullWidth: true,
															required: true,
															name: "date_of_service",
															error: touched?.date_of_service && Boolean(errors?.date_of_service),
															helperText: touched?.date_of_service && errors?.date_of_service,
														},
													}}
												/>
												{values?.date_of_service && (<Tooltip title="Remove Date">
													<IconButton
														className={`closeEndDatePosition`}
														color="primary"
														aria-label="Remove Date"
														onClick={() => { 
															setFieldValue("date_of_service", null, true);
															setFieldTouched("date_of_service", true);
														}}
													>
														<ClearIcon />
													</IconButton>
												</Tooltip>)}
                        <p style={{marginTop: '15px', marginBottom: '15px'}}>Enter the date care will be provided. If you don't know the date yet, you can enter today's date and check again when the date of service is confirmed.</p>
											</Grid>
											<Grid item size={{ xs: 12 }}>
												<Typography variant="h6" component="h6" className="filterFormLabel" style={{fontSize: '14px', fontWeight: 'bold', marginBottom: '5px'}}>
													<label>Procedure Code(s) *</label>
												</Typography>
												<FormControl
													variant="outlined"
													fullWidth
													className={``}
												>
													<Autocomplete
														multiple
														open={openProcedure}
														onOpen={() => setOpenProcedure(true)}
														onClose={() => {
															setInputProcedureValue("");
															setOpenProcedure(false);
														}}
														disableCloseOnSelect
														options={procedureOptions}
														getOptionLabel={(option) =>
															`${option.code} - ${option.title}`
														}
														value={values.procedure_codes}
														onChange={(event, newValue) => {
															const uniqueValues = Array.from(
																new Map(newValue.map(item => [item.code, item])).values()
															);
															setFieldValue("procedure_codes", uniqueValues);
															setInputProcedureValue("");
														}}
														filterOptions={filterOptions}
														inputValue={inputProcedureValue}
														onInputChange={(event, newInputValue, reason) => {
															if (reason === "input") {
																setInputProcedureValue(newInputValue);
																handleInputChange(event, newInputValue, "procedures");
															} else if (reason === "clear") {
																setInputProcedureValue("");
															}
														}}
														renderInput={(params) => (
															<TextField
																{...params}
																inputRef={inputRefProcedure}
																fullWidth
																name="procedureCodes"
																placeholder="Procedure codes"
																variant="outlined"
																error={touched.procedure_codes && Boolean(errors.procedure_codes)}
																helperText={touched.procedure_codes && errors.procedure_codes}
																InputProps={{
																	...params.InputProps,
																	endAdornment: (
																		<>
																			{procedureLoader ? (
																				<CircularProgress color="inherit" size={20} />
																			) : null}
																			{params.InputProps.endAdornment}
																		</>
																	),
																}}
															/>
														)}
														clearOnEscape
														readOnly={procedureLoader}
													/>
												</FormControl>
                        <p style={{marginTop: '15px', marginBottom: '15px'}}>You can type the procedure code your doctor will use to bill for the planned service or item. You can also type part of the code's description to search, for example type "tonsil" to find "Removal of tonsils."</p>
											</Grid>

											<Grid item size={12} style={{textAlign: 'left'}}>
												<Button 
													color="primary" 
													variant="contained" 
													type="submit" 
													style={{ paddingTop: '12px', paddingBottom: '10px' }}
													disabled={submitLoader}
												>
													{submitLoader && ( <CircularProgress color="inherit" size={18} style={{marginRight: '10px'}} /> )}
													Submit
												</Button>
											</Grid>
										</Grid>
									</Grid>
							</form>
						);
					}}
				</Formik>
			</Box>
			<NoteText />
		</LocalizationProvider>
	)
}

export default SearchForm;